import { KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  checkFormEqualWhenDirty,
  checkFormListValidateWhenDirty,
  isStringEmpty,
  stringEmptyDefault,
} from '@common/helpers';
import { RegistrationLabelType } from '@common/helpers/constants/my-booking/passenger-information-input-modal.constant';
import { RepresentativeInformationState } from '@common/helpers/constants/my-booking/passenger-information-input-modal.state';
import { convertPhoneNumber } from '@common/helpers/utilities/convert-character.helper';
import {
  PhoneNumberEventType,
  PhoneTypeEventType,
  RepresentativeInformationData,
  RepresentativeInformationParts,
  RepresentativeInformationPhone,
} from '@common/interfaces/mybooking/modals/passenger-information-input/representative-information.interface';
import { PassengerInformationInputModalCommonService } from '@common/services/my-booking/passenger-information-input-modal/passenger-information-input-modal.common.service';
import { RepresentativeInformationService } from '@common/services/my-booking/passenger-information-input-modal/representative-information.service';
import { SelectComponent } from '@lib/components/base-ui-components/form/select/select.component';
import { AswValidators } from '@lib/helpers/validate/validators';
import { ValidatePhoneNumberOptions, ValidationErrorInfo } from '@lib/interfaces/validate';
import { CommonLibService } from '@lib/services';
import { Subscription } from 'rxjs';

/**
 * representative-information
 * 代表者連絡先情報ブロック
 */
@Component({
  selector: 'asw-representative-information',
  templateUrl: './representative-information.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepresentativeInformationComponent implements OnDestroy {
  @Input()
  get data(): RepresentativeInformationData | undefined {
    return this._data;
  }
  set data(value: RepresentativeInformationData | undefined) {
    this._data = value;
    this.onInit();
  }
  private _data?: RepresentativeInformationData;

  @Output()
  dataChange: EventEmitter<RepresentativeInformationData> = new EventEmitter<RepresentativeInformationData>();

  @Input()
  get parts(): RepresentativeInformationParts | undefined {
    return this._parts;
  }
  set parts(value: RepresentativeInformationParts | undefined) {
    this._parts = value;
    this.onInit();
  }
  private _parts?: RepresentativeInformationParts;

  @Input()
  get isOpen() {
    return this._isOpen;
  }
  set isOpen(value: boolean) {
    this._isOpen = value;
    this._change.markForCheck();
  }
  private _isOpen: boolean = false;

  @Output()
  isOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  public phoneTypeEvent: EventEmitter<PhoneTypeEventType> = new EventEmitter<PhoneTypeEventType>();

  /** 代表者電話番号の値変更処理 */
  @Output()
  public phoneNumberEvent: EventEmitter<PhoneNumberEventType> = new EventEmitter<PhoneNumberEventType>();

  public REPRESENTATIVE_FORMS = {
    EMAIL: 'representativeEmail',
    CONFIRM_EMAIL: 'representativeConfirmEmail',
    PHONE_COUNTRY_CODE: 'phoneCountryCode',
    PHONE_NUMBER: 'phoneNumber',
  };

  public formGroup = new FormGroup<{ [key: string]: FormControl }>({
    // (50.1.1.1.1) 代表者メールアドレス
    [this.REPRESENTATIVE_FORMS.EMAIL]: new FormControl('', [
      AswValidators.required({ params: RepresentativeInformationState.Params.Email }),
      AswValidators.email({ params: RepresentativeInformationState.Params.Email }),
    ]),
    // (50.1.1.1.2) 代表者メールアドレス(確認用)
    [this.REPRESENTATIVE_FORMS.CONFIRM_EMAIL]: new FormControl('', [
      AswValidators.required({ params: RepresentativeInformationState.Params.ConfirmEmail }),
      AswValidators.email({ params: RepresentativeInformationState.Params.ConfirmEmail }),
    ]),
    // (50.1.1.1.4) 代表者電話番号国
    [this.REPRESENTATIVE_FORMS.PHONE_COUNTRY_CODE]: new FormControl('', [
      AswValidators.required({ isNotInput: true, params: RepresentativeInformationState.Params.CountryCode }),
    ]),
    // (50.1.1.1.5) 代表者電話番号
    [this.REPRESENTATIVE_FORMS.PHONE_NUMBER]: new FormControl('', [
      AswValidators.required({ params: RepresentativeInformationState.Params.PhoneNumber }),
    ]),
  });

  public selectedPhoneType: string = '';
  public countryPhoneExtension: string = '';
  public phoneValidatorOption: ValidatePhoneNumberOptions = {
    telCountryCode: this.countryPhoneExtension,
    isSmsSend: false,
    params: RepresentativeInformationState.Params.PhoneNumber,
  };
  public radioBoxErrorInfo: ValidationErrorInfo | string = '';
  public phoneTypeLabels: Array<KeyValue<string, string>> = [];
  public phoneTypeLabelDisplay: Array<string> = [];

  get basicReservationContactInfoEmail() {
    return this.data?.basicReservationContactInfo.value?.email ?? '';
  }

  get basicReservationContactInfoPhoneLabel() {
    const basicReservationContactInfo = this.data?.basicReservationContactInfo.value;
    return basicReservationContactInfo?.phoneNumber;
  }

  get phoneCountries() {
    return this._s04CommonSvc.phoneNumberCountryList;
  }

  private readonly _subscription = new Subscription();

  constructor(
    private readonly _change: ChangeDetectorRef,
    private readonly _service: RepresentativeInformationService,
    private readonly _s04CommonSvc: PassengerInformationInputModalCommonService,
    private readonly _common: CommonLibService
  ) {}

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  /**
   * 初期化処理
   */
  onInit() {
    if (!this.data || !this.parts) {
      return;
    }

    this._subscription.add(this.data.basicReservationContactInfo.subscribe((_) => this._change.markForCheck()));

    if (this.data.emails.length < 2) {
      this.formGroup.controls[this.REPRESENTATIVE_FORMS.EMAIL].setValue(this.data.emails?.[0]?.email);
      this.formGroup.controls[this.REPRESENTATIVE_FORMS.CONFIRM_EMAIL].setValue(this.data.emails?.[0]?.emailConfirm);
    }

    const pd010 = this._s04CommonSvc.masterData.listDataAll.filter(
      (data) => data.data_code === 'PD_010' && data.lang === this._s04CommonSvc.lang
    );
    this.phoneTypeLabels = this._service.createPhoneTypeLabel(pd010 ?? []);
    this.phoneTypeLabelDisplay = this.phoneTypeLabels.map((item) => item.value);

    if (this.data.phones.length < 2) {
      this.formGroup.controls[this.REPRESENTATIVE_FORMS.PHONE_COUNTRY_CODE].setValue(
        this.data.phones?.[0]?.phoneCountry ?? ''
      );
      this.formGroup.controls[this.REPRESENTATIVE_FORMS.PHONE_NUMBER].setValue(this.data.phones?.[0]?.phoneNumber);
      this.countryPhoneExtension =
        this._s04CommonSvc.masterData.i18nCountries?.find(
          (country) => country?.country_2letter_code === this.data?.phones?.[0]?.phoneCountry
        )?.international_tel_country_code ?? '';

      const _selectedPhoneType =
        this.phoneTypeLabels.find((item) => item.key === this.data?.phones?.[0]?.type)?.value ?? '';

      if (!isStringEmpty(_selectedPhoneType)) {
        this.selectedPhoneType = _selectedPhoneType;
      }
    }

    this._change.markForCheck();
  }

  /**
   * (3) 開くボタンボタン押下処理 ※代表者連絡先情報ブロック
   * (6) 閉じるボタン押下処理　※代表者連絡先情報ブロック
   */
  onClickShutter() {
    this.isOpen = !this.isOpen;
    this.isOpenChange.emit(this.isOpen);
  }

  /**
   * 入力値の更新を反映
   * @param requiredCheckError フォームコントロールの更新　trueで必須チェックエラーを起動させる
   */
  update(requiredCheckError: boolean = false) {
    if (this.parts?.registrationLabel === RegistrationLabelType.EDITING) {
      this.formGroup.controls[this.REPRESENTATIVE_FORMS.PHONE_NUMBER].addValidators([
        AswValidators.phoneNumber({
          telCountryCode: this.countryPhoneExtension,
          params: RepresentativeInformationState.Params.PhoneNumber,
        }),
      ]);
      this.data!.isError = this.validateFormData(requiredCheckError);
    }

    this._change.markForCheck();
  }

  validateFormData(requiredCheckError: boolean = false): boolean {
    let isError = false;

    if (
      this.formGroup.controls[this.REPRESENTATIVE_FORMS.PHONE_NUMBER].dirty &&
      this.data?.phones?.[0]?.type === '' &&
      this.parts?.isDisplayPhone &&
      this.data?.phones?.length < 2
    ) {
      this.radioBoxErrorInfo = {
        errorMsgId: 'E0002',
      };
      isError = true;
    } else {
      this.radioBoxErrorInfo = '';
    }

    const checkList: Array<FormControl> = [
      ...(this.data!.emails?.length < 2
        ? [
            this.formGroup.controls[this.REPRESENTATIVE_FORMS.EMAIL],
            this.formGroup.controls[this.REPRESENTATIVE_FORMS.CONFIRM_EMAIL],
          ]
        : []),
      ...(this.parts?.isDisplayPhone && this.data!.phones?.length < 2
        ? [
            this.formGroup.controls[this.REPRESENTATIVE_FORMS.PHONE_COUNTRY_CODE],
            this.formGroup.controls[this.REPRESENTATIVE_FORMS.PHONE_NUMBER],
          ]
        : []),
    ];

    if (checkFormListValidateWhenDirty(checkList, requiredCheckError)) {
      isError = true;
    }

    if (
      !isStringEmpty(this.formGroup.controls[this.REPRESENTATIVE_FORMS.EMAIL].value ?? '') &&
      !isStringEmpty(this.formGroup.controls[this.REPRESENTATIVE_FORMS.CONFIRM_EMAIL].value ?? '') &&
      this.data!.emails?.length < 2 &&
      checkFormEqualWhenDirty(
        this.formGroup.controls[this.REPRESENTATIVE_FORMS.EMAIL],
        this.formGroup.controls[this.REPRESENTATIVE_FORMS.CONFIRM_EMAIL],
        RepresentativeInformationState.Errors.ConfirmEmail
      )
    ) {
      isError = true;
    }

    return isError;
  }

  /**
   * メールアドレス変更時処理
   */
  onChangeEmailAddress() {
    this.data!.emails[0].email = this.formGroup.controls[this.REPRESENTATIVE_FORMS.EMAIL].value ?? '';
    this.dataChange.emit(this.data);
  }

  /**
   * メールアドレス(確認用)変更時処理
   */
  onChangeEmailConfirmAddress() {
    this.data!.emails[0].emailConfirm = this.formGroup.controls[this.REPRESENTATIVE_FORMS.CONFIRM_EMAIL].value ?? '';
  }

  /**
   * (4) 代表者電話番号種別の値変更処理
   * (4.1) 画面項目出力編集に従い、選択した代表者電話番号種別の値に変更する。
   * @param event
   */
  onChangePhoneType(event: string): void {
    const _selectedPhoneType = this.phoneTypeLabels.find((item) => item.value === event)?.key ?? '';

    if (_selectedPhoneType !== '') {
      const phoneType = this._service.getApiPhoneType(_selectedPhoneType);

      this.checkRepresentativeData();

      const oldPhoneType = this.data!.phones[0].type;

      if (oldPhoneType !== phoneType) {
        this.data!.phones[0].type = phoneType;
        this.dataChange.emit(this.data);
        this.phoneTypeEvent.emit({
          oldPhoneType: oldPhoneType,
          newPhoneType: phoneType,
        });
      }
    }
  }

  /**
   * 電話番号国変更処理
   * @param event
   */
  onChangeCountry(event: SelectComponent) {
    this.countryPhoneExtension =
      this._s04CommonSvc.masterData.i18nCountries?.find((country) => country?.country_2letter_code === event.data)
        ?.international_tel_country_code ?? '';
    this.data!.phones[0].phoneCountry =
      this.formGroup.controls[this.REPRESENTATIVE_FORMS.PHONE_COUNTRY_CODE].value ?? '';
    this.data!.phones[0].phoneCountryNumber = this.countryPhoneExtension;
    this.dataChange.emit(this.data);
  }

  /**
   * 電話番号変更時イベント
   */
  onChangePhoneNumber() {
    const phoneNumber = this.formGroup.controls[this.REPRESENTATIVE_FORMS.PHONE_NUMBER].value ?? '';
    const phoneCountry = this.data?.phones[0].phoneCountry ?? '';

    this.data!.phones[0].phoneNumber = phoneNumber;
    this.dataChange.emit(this.data);
    this.phoneNumberEvent.emit({
      phoneCountry: phoneCountry,
      newPhoneNumber: phoneNumber,
    });
  }

  /**
   * (3) 反映(代表者連絡先情報)ボタン押下処理　※予約基本情報詳細(代表者連絡先情報)グループ
   */
  onClickEnterBasicReservationContactInfo() {
    this.formGroup.controls[this.REPRESENTATIVE_FORMS.EMAIL].setValue(
      this.data?.basicReservationContactInfo.value?.email ?? ''
    );
    this.formGroup.controls[this.REPRESENTATIVE_FORMS.CONFIRM_EMAIL].setValue(
      this.data?.basicReservationContactInfo.value?.emailConfirm ?? ''
    );
    this.formGroup.controls[this.REPRESENTATIVE_FORMS.PHONE_COUNTRY_CODE].setValue(
      this.data?.basicReservationContactInfo.value?.phoneCountry ?? ''
    );
    this.countryPhoneExtension =
      this._s04CommonSvc.masterData.i18nCountries.find(
        (country) => country.country_2letter_code === this.data?.basicReservationContactInfo.value?.phoneCountry
      )?.international_tel_country_code ?? '';
    this.formGroup.controls[this.REPRESENTATIVE_FORMS.PHONE_NUMBER].setValue(
      convertPhoneNumber(
        this.data?.basicReservationContactInfo.value?.phoneNumber ?? '',
        this._common.isJapaneseOffice()
      )
    );

    if (this.data!.emails?.length < 2) {
      this.data!.emails[0].email = this.formGroup.controls[this.REPRESENTATIVE_FORMS.EMAIL].value ?? '';
      this.data!.emails[0].emailConfirm = this.formGroup.controls[this.REPRESENTATIVE_FORMS.CONFIRM_EMAIL].value ?? '';
    }

    if (this.parts?.isDisplayPhone && this.data!.phones?.length < 2) {
      this.data!.phones[0].phoneCountry =
        this.formGroup.controls[this.REPRESENTATIVE_FORMS.PHONE_COUNTRY_CODE].value ?? '';
      this.data!.phones[0].phoneCountryNumber = this.countryPhoneExtension;
      this.data!.phones[0].phoneNumber = this.formGroup.controls[this.REPRESENTATIVE_FORMS.PHONE_NUMBER].value ?? '';
    }

    this.dataChange.emit(this.data);
  }

  checkRepresentativeData() {
    if (!this.data) {
      this._service.initialData();
    }
  }

  /**
   * 外部呼出し用 強制refresh
   */
  refreshForce() {
    this.onInit();
  }

  /**
   * 外部呼出し用 強制update
   */
  updateForce(requiredCheckError: boolean = false) {
    this.update(requiredCheckError);
  }

  getPhoneInfoLabel(phone: RepresentativeInformationPhone): string {
    const _phoneTypeLabel = this.phoneTypeLabels.find((item) => item.key === phone.type)?.value;
    const _phoneTypeLabelDisplay = stringEmptyDefault(_phoneTypeLabel);
    const _phoneCountryNumberLabel = !isStringEmpty(phone.phoneCountryNumber) ? ` +${phone.phoneCountryNumber}` : '';

    return `${_phoneTypeLabelDisplay}${_phoneCountryNumberLabel} ${phone.phoneNumber}`;
  }
}
